import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";
import { useRouteIncidents } from "@/hooks/useRouteIncidents";
import { styleFor, incidentDivIcon, incidentPopupHtml, prioritiseIncidents } from "@/lib/incidents";
import { vanMarkerImageUrl, vanDivHtml, vehiclePopupHtml } from "@/lib/liveVehicles";
import { useLiveVehicles } from "@/hooks/useLiveVehicles";
import LiveVehiclesToggle from "@/components/LiveVehiclesToggle";
import IncidentWarning from "@/components/IncidentWarning";
import MapLegend from "@/components/MapLegend";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const TRANSPARENT_PX = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

// Shared stop-popup HTML (used by both engines).
const stopPopupHtml = (l, i) => [
  `<div style="min-width:180px">`,
  `<div style="font-weight:700;font-size:13px">${i + 1}. ${l.title || "Stop"}</div>`,
  l.postcode ? `<div style="color:#64748b;font-size:11px;margin-bottom:5px">${l.postcode}</div>` : "",
  `<div style="font-size:12px;line-height:1.6">`,
  l.arrival ? `Arrive: <b>${l.arrival}</b>${l.fixed_time ? ` · fixed ${l.fixed_time}` : ""}<br/>` : "",
  (l.onsite_start || l.departure) ? `On-site: ${l.onsite_start || "?"}–${l.departure || "?"}<br/>` : "",
  l.break_mins > 0 ? `Break: +${l.break_mins} min<br/>` : "",
  l.travel_mins != null ? `Drive in: ${l.travel_mins}m${l.travel_miles != null ? ` (${l.travel_miles} mi)` : ""}` : "",
  `</div>`,
  l.late_for_fixed ? `<div style="color:#b45309;font-size:11px;margin-top:4px">⚠ Arrives after the fixed ${l.fixed_time || ""} appointment</div>` : "",
  `</div>`,
].join("");

// Google run/day map: route line + numbered stops + live traffic + incident markers.
// Falls back to the Leaflet map via onFail on any Google load/auth/quota error.
function GoogleLegsMap({ legs = [], routeGeom, display = [], height = 300, testid, onFail, vehicles = [], fitVehicles = false, focusVehicleId }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const objsRef = useRef([]);
  const incRef = useRef([]);
  const vehRef = useRef([]);
  const vehInfoRef = useRef(null);
  const vehMarkerMapRef = useRef({});
  const infoRef = useRef(null);
  const roRef = useRef(null);
  const boundsRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setOptions({ key: MAPS_KEY, v: "weekly" });
    const fail = (why) => { if (!cancelled && onFail) onFail(why || "authentication error (invalid key / RefererNotAllowed / billing / quota)"); };
    // Fan out to EVERY mounted map so a later mount can't clobber earlier handlers.
    if (!window.__gmFailCbs) { window.__gmFailCbs = new Set(); window.gm_authFailure = () => window.__gmFailCbs.forEach((f) => f()); }
    window.__gmFailCbs.add(fail);
    const timer = setTimeout(() => { if (!cancelled && !(window.google && window.google.maps)) fail("Maps JS API did not load within 8s (network / CSP / script blocked)"); }, 8000);
    Promise.all([importLibrary("maps"), importLibrary("marker")]).then(() => {
      if (cancelled || !ref.current || !window.google) return;
      clearTimeout(timer);
      const g = window.google;
      const map = new g.maps.Map(ref.current, {
        center: { lat: 53.8, lng: -2.5 }, zoom: 6,
        mapTypeControl: false, streetViewControl: false, fullscreenControl: false, clickableIcons: false,
      });
      new g.maps.TrafficLayer().setMap(map);
      infoRef.current = new g.maps.InfoWindow();
      mapRef.current = map;
      setReady(true);
      if (ref.current && "ResizeObserver" in window) {
        const ro = new ResizeObserver(() => {
          if (!mapRef.current) return;
          g.maps.event.trigger(mapRef.current, "resize");
          if (boundsRef.current && !boundsRef.current.isEmpty()) mapRef.current.fitBounds(boundsRef.current, 48);
        });
        ro.observe(ref.current);
        roRef.current = ro;
      }
    }).catch((e) => { clearTimeout(timer); fail("importLibrary error: " + (e && e.message ? e.message : String(e))); });
    return () => { cancelled = true; clearTimeout(timer); if (window.__gmFailCbs) window.__gmFailCbs.delete(fail); if (roRef.current) roRef.current.disconnect(); };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.google) return;
    const g = window.google.maps;
    objsRef.current.forEach((o) => o.setMap(null));
    objsRef.current = [];
    const bounds = new g.LatLngBounds();
    const pts = (legs || []).filter((l) => l.lat && l.lng).map((l) => [l.lat, l.lng]);
    const line = (routeGeom && routeGeom.length > 1) ? routeGeom : pts;
    if (line.length > 1) {
      const path = line.map(([lat, lng]) => ({ lat, lng }));
      const pl = new g.Polyline({ path, strokeColor: "#4F46E5", strokeWeight: 4, strokeOpacity: 0.85, map });
      objsRef.current.push(pl);
    }
    (legs || []).forEach((l, i) => {
      if (!l.lat || !l.lng) return;
      const pos = { lat: l.lat, lng: l.lng };
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="26" height="26"><circle cx="13" cy="13" r="11" fill="#4F46E5" stroke="#fff" stroke-width="2"/></svg>`;
      const m = new g.Marker({
        position: pos, map, title: `${i + 1}. ${l.title || ""}`,
        label: { text: String(i + 1), color: "#fff", fontSize: "11px", fontWeight: "700" },
        icon: { url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
          scaledSize: new g.Size(26, 26), labelOrigin: new g.Point(13, 13) },
        zIndex: 500,
      });
      m.addListener("click", () => { infoRef.current.setContent(stopPopupHtml(l, i)); infoRef.current.open(map, m); });
      objsRef.current.push(m);
      bounds.extend(pos);
    });
    boundsRef.current = bounds.isEmpty() ? null : bounds;
    if (!bounds.isEmpty()) map.fitBounds(bounds, 48);
  }, [legs, routeGeom, ready]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.google) return;
    const g = window.google.maps;
    incRef.current.forEach((o) => o.setMap(null));
    incRef.current = [];
    if (!infoRef.current) infoRef.current = new g.InfoWindow();
    (display || []).forEach((inc) => {
      if (!inc.point) return;
      const m = new g.Marker({
        position: { lat: inc.point.lat, lng: inc.point.lng }, map,
        label: { text: styleFor(inc.kind).emoji, fontSize: "20px" },
        icon: { url: TRANSPARENT_PX, scaledSize: new g.Size(1, 1), labelOrigin: new g.Point(0, 0) },
        opacity: inc.affects ? 1 : 0.6, zIndex: 999,
      });
      m.addListener("click", () => { infoRef.current.setContent(incidentPopupHtml(inc)); infoRef.current.open(map, m); });
      incRef.current.push(m);
    });
  }, [display, ready]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !window.google) return;
    const g = window.google.maps;
    vehRef.current.forEach((o) => o.setMap(null));
    vehRef.current = [];
    vehMarkerMapRef.current = {};
    if (!vehInfoRef.current) vehInfoRef.current = new g.InfoWindow();
    const vb = new g.LatLngBounds();
    (vehicles || []).forEach((v) => {
      if (v.lat == null || v.lng == null) return;
      const pos = { lat: v.lat, lng: v.lng };
      const m = new g.Marker({
        position: pos, map,
        title: v.registration || v.description || v.vehicleId,
        icon: { url: vanMarkerImageUrl(v), scaledSize: new g.Size(54, 54), anchor: new g.Point(27, 27) },
        zIndex: 800,
      });
      m.addListener("click", () => { vehInfoRef.current.setContent(vehiclePopupHtml(v)); vehInfoRef.current.open(map, m); });
      vehRef.current.push(m);
      vehMarkerMapRef.current[String(v.vehicleId)] = { m, pos, v };
      vb.extend(pos);
    });
    if (fitVehicles && (vehicles || []).length && !vb.isEmpty()) map.fitBounds(vb, 60);
  }, [vehicles, fitVehicles, ready]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focusVehicleId) return;
    const rec = vehMarkerMapRef.current[String(focusVehicleId).split("#")[0]];
    if (!rec) return;
    map.panTo(rec.pos);
    map.setZoom(13);
    if (vehInfoRef.current) { vehInfoRef.current.setContent(vehiclePopupHtml(rec.v)); vehInfoRef.current.open(map, rec.m); }
  }, [focusVehicleId]);

  return <div ref={ref} style={{ height }} className="w-full rounded-xl border border-slate-200 overflow-hidden" data-testid={testid} />;
}

// Leaflet run/day map (keyless OSM fallback): route line + numbered stops + incident markers.
function LeafletLegsMap({ legs = [], routeGeom, display = [], height = 300, testid, vehicles = [], fitVehicles = false, focusVehicleId }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const routeLayer = useRef(null);
  const incLayer = useRef(null);
  const vehLayer = useRef(null);
  const vehMarkerMapRef = useRef({});

  useEffect(() => {
    if (!ref.current || mapRef.current) return undefined;
    const map = L.map(ref.current, { scrollWheelZoom: false });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      { attribution: "&copy; OpenStreetMap", maxZoom: 19 }).addTo(map);
    mapRef.current = map;
    routeLayer.current = L.layerGroup().addTo(map);
    incLayer.current = L.layerGroup().addTo(map);
    vehLayer.current = L.layerGroup().addTo(map);
    map.setView([53.8, -2.5], 6);
    const t = setTimeout(() => map.invalidateSize(), 150);
    return () => { clearTimeout(t); map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current, layer = routeLayer.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const pts = (legs || []).filter((l) => l.lat && l.lng).map((l) => [l.lat, l.lng]);
    const line = (routeGeom && routeGeom.length > 1) ? routeGeom : pts;
    if (line.length > 1) L.polyline(line, { color: "#4F46E5", weight: 4, opacity: 0.8 }).addTo(layer);
    (legs || []).forEach((l, i) => {
      if (!l.lat || !l.lng) return;
      L.marker([l.lat, l.lng], {
        icon: L.divIcon({
          className: "",
          html: `<div style="background:#4F46E5;color:#fff;width:22px;height:22px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.3)">${i + 1}</div>`,
          iconSize: [22, 22], iconAnchor: [11, 11],
        }),
      }).addTo(layer)
        .bindPopup(stopPopupHtml(l, i), { maxWidth: 260 })
        .bindTooltip(`${i + 1}. ${l.title || ""}`);
    });
    if (pts.length) map.fitBounds(pts, { padding: [40, 40], maxZoom: 11 });
    setTimeout(() => map.invalidateSize(), 120);
  }, [legs, routeGeom]);

  useEffect(() => {
    const layer = incLayer.current;
    if (!layer) return;
    layer.clearLayers();
    (display || []).forEach((inc) => {
      if (!inc.point) return;
      L.marker([inc.point.lat, inc.point.lng], { icon: incidentDivIcon(L, inc), zIndexOffset: 1000 })
        .addTo(layer)
        .bindPopup(incidentPopupHtml(inc), { maxWidth: 280 });
    });
  }, [display]);

  useEffect(() => {
    const map = mapRef.current, layer = vehLayer.current;
    if (!map || !layer) return;
    layer.clearLayers();
    vehMarkerMapRef.current = {};
    const pts = [];
    (vehicles || []).forEach((v) => {
      if (v.lat == null || v.lng == null) return;
      const mk = L.marker([v.lat, v.lng], {
        icon: L.divIcon({ className: "", html: vanDivHtml(v), iconSize: [54, 54], iconAnchor: [27, 27] }),
        zIndexOffset: 800,
      }).addTo(layer).bindPopup(vehiclePopupHtml(v), { maxWidth: 260 }).bindTooltip(v.registration || v.description || "");
      vehMarkerMapRef.current[String(v.vehicleId)] = mk;
      pts.push([v.lat, v.lng]);
    });
    if (fitVehicles && pts.length) map.fitBounds(pts, { padding: [50, 50], maxZoom: 12 });
  }, [vehicles, fitVehicles]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focusVehicleId) return;
    const mk = vehMarkerMapRef.current[String(focusVehicleId).split("#")[0]];
    if (!mk) return;
    map.setView(mk.getLatLng(), 13);
    mk.openPopup();
  }, [focusVehicleId]);

  return <div ref={ref} style={{ height }} className="w-full rounded-xl border border-slate-200 overflow-hidden" data-testid={testid} />;
}

// Run/day map. Prefers Google Maps (live traffic) when a browser key is set,
// and degrades to the keyless Leaflet map on any Google load/auth/quota failure.
export default function RouteMapLeaflet({ legs = [], height = 300, testid = "run-map", dayScan, fitVehicles = false, fill = false, focusVehicleId, restrictRegistrations, providersOverride }) {
  const controlled = dayScan !== undefined && dayScan !== null;
  const [showAll, setShowAll] = useState(true);
  const [failReason, setFailReason] = useState(null);
  const { vehicles, enabled: vansOn, available: vansAvail } = useLiveVehicles();
  const normReg = (r) => (r || "").replace(/\s+/g, "").toUpperCase();
  const restrictSet = restrictRegistrations && restrictRegistrations.length ? new Set(restrictRegistrations.map(normReg)) : null;
  const allLive = (vansOn && vansAvail) ? vehicles : [];
  const liveVehicles = restrictSet ? allLive.filter((v) => restrictSet.has(normReg(v.registration))) : allLive;
  const mapHeight = fill ? "100%" : height;
  const debug = typeof window !== "undefined" && window.localStorage && localStorage.getItem("htss_maps_debug") === "1";
  const points = (legs || []).filter((l) => l.lat && l.lng).map((l) => [l.lat, l.lng]);
  const { data: fetched } = useRouteIncidents(points, { snapToRoads: true, enabled: !controlled || showAll, showAll });

  const data = controlled
    ? { enabled: dayScan.enabled !== false, incidents: dayScan.incidents || [], summary: dayScan.summary, reason: dayScan.reason, providers: dayScan.providers }
    : fetched;
  const markerData = (controlled && showAll && fetched) ? fetched : data;
  const suggestedDeparture = controlled ? dayScan.suggested_departure : null;
  const routeGeom = controlled ? dayScan.route_geometry : fetched?.route_geometry;
  const prioritised = prioritiseIncidents(markerData?.incidents || [], { cap: 12, showAll });

  const useGoogle = MAPS_KEY && !failReason;

  return (
    <div className={fill ? "h-full flex flex-col" : ""}>
      {!fill && <IncidentWarning data={data} suggestedDeparture={suggestedDeparture} />}
      <div className={`relative ${fill ? "flex-1 min-h-0" : ""}`}>
        {useGoogle ? (
          <GoogleLegsMap legs={legs} routeGeom={routeGeom} display={prioritised.display} height={mapHeight} testid={testid} vehicles={liveVehicles} fitVehicles={fitVehicles} focusVehicleId={focusVehicleId} onFail={(why) => setFailReason(why || "unknown")} />
        ) : (failReason && debug) ? (
          <div style={{ height: mapHeight, display: "flex", alignItems: "center", justifyContent: "center", background: "#fef2f2", color: "#b91c1c", padding: 16, textAlign: "center", whiteSpace: "pre-wrap", fontSize: 13, borderRadius: 12, border: "1px solid #fecaca" }} data-testid="run-map-error">
            {`Google Maps failed to initialise\nReason: ${failReason}`}
          </div>
        ) : (
          <LeafletLegsMap legs={legs} routeGeom={routeGeom} display={prioritised.display} height={mapHeight} testid={testid} vehicles={liveVehicles} fitVehicles={fitVehicles} focusVehicleId={focusVehicleId} />
        )}
        <LiveVehiclesToggle />
        <MapLegend counts={prioritised.counts} hidden={prioritised.hidden} showAll={showAll} onToggle={setShowAll} providers={providersOverride || data?.providers} googleTraffic={useGoogle} />
      </div>
    </div>
  );
}
